export const JOB_TYPES = ['LOCAL', 'LONG_DISTANCE', 'LABOR_ONLY', 'DELIVERY', 'PICKUP', 'PACKING', 'DUMP_RUN']

// ── QUICK DISPATCH (2026-08-06) ──
// Small job types that skip the estimate funnel: one Quick Dispatch form
// creates a minimal already-booked estimate (the billing anchor — Records/
// invoices/rate math all key off the estimate) + the dispatch job in one
// save. Surfaces: client intake auto-offer, client-file header button,
// dispatch board NEW cards. Real moves (LOCAL/LONG_DISTANCE/PACKING) keep
// the full estimate path.
export const QUICK_DISPATCH_TYPES = ['DELIVERY', 'DUMP_RUN', 'LABOR_ONLY', 'PICKUP']
// Smart prefill: DELIVERY = the FYN delivery rate; everything else falls
// back to config.default_hourly_rate (225) at the call site. Hours default
// small — these are small jobs by definition. All editable on the form.
export const QUICK_DISPATCH_DEFAULTS = {
  DELIVERY: { rate: 125, hours: 2, crew: 2 },
  DUMP_RUN: { rate: null, hours: 2, crew: 2 },
  LABOR_ONLY: { rate: null, hours: 4, crew: 3 },
  PICKUP: { rate: null, hours: 2, crew: 2 },
}

// ── TRUCK SIZE PRESETS ──
// Shared between LOCAL and OUT-OF-STATE estimate creation (May 2026 sales-
// lead request to unify the two). Two label flavors:
//   • TRUCK_TYPE_OPTIONS         — verbose labels for the spacious creation
//                                  form in ClientPricingTab.
//   • TRUCK_TYPE_OPTIONS_COMPACT — short labels for the dense edit popover
//                                  in EstimateModal.
// 20ft added per sales-lead spec — needed for jobs that don't fill a 26'
// but are too big for a 16'. The `'value'` keys (16ft / 20ft / 26ft /
// none) are what gets written to estimates.trucks_needed and
// estimates.extra_trucks; UI labels can change without touching data.
//
// 2026-09-11 (Jaylen): '2x26ft' RETIRED as a primary size. A second truck
// is always an EXTRA now (26' + extra 26' → rate-table rate for the crew +
// 26', plus the extra-truck surcharge per billed hour). Existing rows were
// migrated (sql/090); `truckSizeShortLabel` still renders the old key.
export const TRUCK_TYPE_OPTIONS = [
  { value: 'none',   label: 'No Truck (Labor Only)' },
  { value: '16ft',   label: "16' Truck" },
  { value: '20ft',   label: "20' Truck" },
  { value: '26ft',   label: "26' Truck" },
]

export const TRUCK_TYPE_OPTIONS_COMPACT = [
  { value: 'none',   label: 'No Truck' },
  { value: '16ft',   label: "16'" },
  { value: '20ft',   label: "20'" },
  { value: '26ft',   label: "26'" },
]

// Sizes available as "extras" — multiples on top of the primary truck.
// Excludes 'none' and combo sizes since extras are always single trucks.
export const TRUCK_EXTRA_SIZES = [
  { value: '16ft', label: "16' Truck" },
  { value: '20ft', label: "20' Truck" },
  { value: '26ft', label: "26' Truck" },
]

// Map a truck size key (16ft / 20ft / 26ft / 2x26ft / none) to a short
// display string. Centralized so we don't keep re-writing
// `t === '16ft' ? "16'" : "26'"` ternaries that miss new sizes.
export const truckSizeShortLabel = (v) => {
  switch (v) {
    case '16ft':   return "16'"
    case '20ft':   return "20'"
    case '26ft':   return "26'"
    case '2x26ft': return "2x26'" // legacy key — no longer selectable
    case 'none':   return 'No Truck'
    default:       return v || ''
  }
}
// ── Estimate pricing/truck display helpers (QA Module-2 BUG-3/BUG-8) ──
// The stored totals ALWAYS price local labor at the EFFECTIVE rate
// (base + extra-truck surcharge unless waived). Read surfaces must use
// these helpers so displayed line items sum to the stored total. Keep
// api/public-estimate.js's copies in sync (server-side CJS can't import
// this file).

// estimates.extra_trucks is a JSON text column ('["16ft"]').
export const estimateExtraTrucks = (est) => {
  try {
    const a = JSON.parse(est?.extra_trucks || '[]')
    return Array.isArray(a) ? a.filter(Boolean) : []
  } catch { return [] }
}

// Per-hour fee for each extra truck, from app_config (default $25).
export const extraTruckFeePerHrFrom = (config) => {
  try { return parseFloat(JSON.parse(config?.extra_truck_fee ?? '25')) || 0 } catch { return 25 }
}

// Per-hour surcharge for an estimate's extra trucks (0 when waived or OOS).
export const estimateExtraTruckSurcharge = (est, config) => {
  if (!est || est.job_type === 'LONG_DISTANCE' || est.extra_truck_fee_waived) return 0
  return estimateExtraTrucks(est).length * extraTruckFeePerHrFrom(config)
}

// The rate every billed labor hour is actually priced at.
export const estimateEffectiveRate = (est, config) =>
  (parseFloat(est?.hourly_rate) || 0) + estimateExtraTruckSurcharge(est, config)

// "26' + 16'" — primary + extras, short labels. Empty string for no truck.
export const estimateTruckLabel = (est) => {
  const parts = []
  if (est?.trucks_needed && est.trucks_needed !== 'none') parts.push(truckSizeShortLabel(est.trucks_needed))
  estimateExtraTrucks(est).forEach(t => parts.push(truckSizeShortLabel(t)))
  return parts.join(' + ')
}

// Must mirror DEFAULT_OPERATIONS_STATUSES keys AND the DB constraint
// move_jobs_status_check (sql/069) — all three lists carry in_progress.
export const JOB_STATUSES = ['unassigned', 'assigned', 'dispatched', 'in_progress', 'completed', 'cancelled']
export const LEAD_SOURCES = ['website', 'referral', 'manual', 'repeat', 'google', 'yelp']

// ── THREE-STATUS SYSTEM ──
//
// Each status has an `allowed_next` array — the set of status keys the
// UI considers a "normal" forward transition. Transitioning to a status
// NOT in allowed_next is still possible but flagged as a "danger
// transition" — the UI dims/warns and the EstimateModal prompts a
// confirmation dialog before firing the side effects (job creation,
// SMS/push notifications, etc.).
//
// Helper functions (isTransitionAllowed, getNextStatuses) below.

// Default color palette (refreshed 2026-05-14). Constraints:
//   • Client + (Estimate XOR Operations) are visible together on the
//     Clients page row. Colors within those same-row pairs must be
//     visually distinct.
//   • Estimate and Operations are never visible together (the row
//     swaps to Operations once an estimate is booked + job exists), so
//     they may overlap conceptually-equivalent statuses (e.g. both
//     "Cancelled" can lean red).
//   • Every color has luminance chosen so the existing statusTextColor
//     contrast helper picks readable text (white for dark colors,
//     black for warm bright colors).
//
// All `bg` fields are the 50-ish Tailwind tint of the same hue, used for
// soft pill backgrounds in places like the EstimateModal status legend.

// 1. Client Status (person lifecycle) — fixed 3 values
export const DEFAULT_CLIENT_STATUSES = [
  { key: 'new_lead', label: 'New Lead', color: '#3b82f6', bg: '#eff6ff', allowed_next: ['working', 'inactive'] },  // blue-500
  { key: 'working',  label: 'Working',  color: '#f59e0b', bg: '#fffbeb', allowed_next: ['inactive'] },              // amber-500
  // 'inactive' is the lead-drop terminal. Re-engaging requires explicit
  // forward move back to 'working' — the UI will warn since it's unusual.
  { key: 'inactive', label: 'Inactive', color: '#64748b', bg: '#f1f5f9', allowed_next: ['working'] },              // slate-500
]

// 2. Estimate Status (sales lifecycle) — configurable in Settings
// The sales pipeline for BBM (May 2026 rewrite per sales-lead input):
//   Building → Ready → Quoted → Waiting on Client → Follow-Up
//   → Booked → Completed (+ Cancelled as terminal failure)
//
// What each stage means in plain English:
//   • Building          — estimate is actively being built (room measurements,
//                         pricing, line items being entered). Internal-only.
//   • Ready             — built and reviewed; ready to be quoted to the client.
//                         Handoff state from estimator → sales person.
//   • Quoted            — quote was delivered (verbal or written).
//   • Pending Approval  — quote delivered, waiting on client to greenlight
//                         (think about it, talk to spouse, etc.).
//   • Follow-Up        — actively needs a nudge — oldest pending-approval
//                         rows, or sales person manually flagged.
//   • Booked            — closed-won, move on the dispatch board.
//   • Completed         — job delivered + billed; sales pipeline terminal.
//   • Cancelled         — fell through at any stage; terminal failure.
//
// `new` and `lost` from the previous schema were retired in the rewrite:
//   • new   → migrated to `building` (same intent, clearer name)
//   • lost  → migrated to `cancelled` (single failure terminal is enough)
//
// Booked → Completed is auto-transitioned by the store when the FINAL
// PHASE (highest phase_sequence) of the estimate's move_jobs flips to
// ops status 'completed'. Earlier phases don't trigger it; incomplete
// stragglers (e.g. a never-assigned pack day) don't block it.
// See saveJob in StoreContext.jsx for the hook. Manual flips still work
// for one-off cases (e.g. labor-only jobs without a dispatch row).
//
// Booked is a near-terminal state for the SALES pipeline — once booked,
// the dispatch board owns operational state. Reverting from booked back
// to quoted has dangerous side effects (the original auto-push already
// happened, the existing move_jobs row is on the board, the audit log
// will show every flip). allowed_next from 'booked' is
// ['completed', 'cancelled'] — any other backward transition triggers
// a confirmation dialog.
export const DEFAULT_ESTIMATE_STATUSES = [
  { key: 'building',          label: 'Building',          color: '#8b5cf6', bg: '#f5f3ff', allowed_next: ['ready', 'quoted', 'cancelled'] },                              // violet-500 — work in progress
  { key: 'ready',             label: 'Ready',             color: '#6366f1', bg: '#eef2ff', allowed_next: ['quoted', 'building', 'cancelled'] },                           // indigo-500 — ready to present
  { key: 'quoted',            label: 'Quoted',            color: '#eab308', bg: '#fefce8', allowed_next: ['pending_approval', 'follow_up', 'booked', 'postponed', 'cancelled'] },     // yellow-500 — quote delivered
  { key: 'pending_approval',  label: 'Pending Approval',  color: '#d97706', bg: '#fffbeb', allowed_next: ['follow_up', 'booked', 'postponed', 'cancelled'] },                         // amber-600 — client weighing options
  { key: 'follow_up',         label: 'Follow-Up',         color: '#ea580c', bg: '#fff7ed', allowed_next: ['pending_approval', 'booked', 'postponed', 'cancelled'] },                   // orange-600 — needs a nudge
  { key: 'booked',            label: 'Booked',            color: '#059669', bg: '#ecfdf5', allowed_next: ['completed', 'postponed', 'cancelled'] },                                    // emerald-600 — closed-won, job on the board
  { key: 'completed',         label: 'Completed',         color: '#166534', bg: '#f0fdf4', allowed_next: [] },                                                            // green-800 — job delivered + billed, terminal
  { key: 'cancelled',         label: 'Cancelled',         color: '#ef4444', bg: '#fef2f2', allowed_next: [] },                                                            // red-500 — terminal failure
  { key: 'postponed',         label: 'Postponed',         color: '#0369a1', bg: '#e0f2fe', allowed_next: ['quoted', 'booked', 'cancelled'] },                            // sky-700 — on hold, no date (2026-09-21): keeps its quote, off the board + capacity; back to Quoted / Booked when they reschedule
  { key: 'declined',          label: 'Declined',          color: '#94a3b8', bg: '#f1f5f9', allowed_next: ['quoted', 'booked'] },                                         // slate-400 — an option the client didn't pick (moves-architecture.md); tucked away, never deleted
]

// 3. Operations Status (job lifecycle) — configurable in Settings
// Distinct palette from the Estimate pipeline so an at-a-glance scan tells
// you whether you're looking at a sales-side or ops-side status.
export const DEFAULT_OPERATIONS_STATUSES = [
  { key: 'unassigned', label: 'Unassigned', color: '#52525b', bg: '#f4f4f5', allowed_next: ['assigned', 'cancelled'] },               // zinc-600 — dark gray, white text (was light slate w/ readability concern)
  { key: 'assigned',   label: 'Assigned',   color: '#0ea5e9', bg: '#f0f9ff', allowed_next: ['dispatched', 'unassigned', 'cancelled'] }, // sky-500 — distinct from client New Lead blue
  { key: 'dispatched', label: 'Dispatched', color: '#6366f1', bg: '#eef2ff', allowed_next: ['in_progress', 'completed', 'cancelled'] }, // indigo-500 — in motion
  { key: 'in_progress',label: 'In Progress',color: '#0891b2', bg: '#ecfeff', allowed_next: ['completed', 'cancelled'] },               // cyan-600 — working
  { key: 'completed',  label: 'Completed',  color: '#16a34a', bg: '#f0fdf4', allowed_next: [] },                                       // green-600 — successfully done
  { key: 'cancelled',  label: 'Cancelled',  color: '#f43f5e', bg: '#fff1f2', allowed_next: ['unassigned'] },                           // rose-500 — distinct from estimate cancelled red
]

// 4. Priority tiers (sql/047) — manual judgment set by brothers when
// finalizing the estimate. Drives queue ordering on the dashboard. Same
// shape as a status (key/label/color/bg) so the StatusManager UI can edit
// it with the same renderer used for the three status systems.
export const DEFAULT_PRIORITY_OPTIONS = [
  { key: 'low',      label: 'Low',      color: '#94a3b8', bg: '#f1f5f9' },  // slate-400
  { key: 'medium',   label: 'Medium',   color: '#3b82f6', bg: '#eff6ff' },  // blue-500 — default for most new estimates
  { key: 'high',     label: 'High',     color: '#f59e0b', bg: '#fffbeb' },  // amber-500
  { key: 'critical', label: 'Critical', color: '#dc2626', bg: '#fef2f2' },  // red-600
]

// 5. Furniture quality tiers (sql/048) — industry-standard Good/Better/
// Best/Luxury scale. Client-level descriptor; drives internal crew + care
// allocation decisions.
export const DEFAULT_FURNITURE_CLASS_OPTIONS = [
  { key: 'good',    label: 'Good',    color: '#a8a29e', bg: '#f5f5f4' },  // stone-400 — entry-level
  { key: 'better',  label: 'Better',  color: '#14b8a6', bg: '#f0fdfa' },  // teal-500 — mid-range
  { key: 'best',    label: 'Best',    color: '#10b981', bg: '#ecfdf5' },  // emerald-500 — high-end
  { key: 'luxury',  label: 'Luxury',  color: '#8b5cf6', bg: '#f5f3ff' },  // violet-500 — bespoke
]

// Helpers to load status configs from app_config (with fallback to defaults)
export const getClientStatuses = (config) => {
  if (config?.client_statuses) {
    try { return JSON.parse(config.client_statuses) } catch(e) {}
  }
  return DEFAULT_CLIENT_STATUSES
}

// The Settings list is a snapshot taken when it was saved; statuses added
// to the defaults since then (declined 2026-09-18, postponed 2026-09-21)
// would otherwise be invisible to every dropdown and chip row. Merge:
// keep the saved rows (colors, order, allowed_next edits), append any
// default the snapshot doesn't know, and let saved rows transition to
// those new statuses wherever the default says they can.
export const getEstimateStatuses = (config) => {
  if (config?.estimate_statuses) {
    try {
      const saved = JSON.parse(config.estimate_statuses)
      if (Array.isArray(saved) && saved.length) {
        const known = new Set(saved.map(s => s.key))
        const missing = DEFAULT_ESTIMATE_STATUSES.filter(d => !known.has(d.key))
        if (!missing.length) return saved
        const newKeys = new Set(missing.map(d => d.key))
        const merged = saved.map(s => {
          const d = DEFAULT_ESTIMATE_STATUSES.find(x => x.key === s.key)
          const extra = (d?.allowed_next || []).filter(k => newKeys.has(k) && !(s.allowed_next || []).includes(k))
          return extra.length ? { ...s, allowed_next: [...(s.allowed_next || []), ...extra] } : s
        })
        return [...merged, ...missing]
      }
    } catch(e) {}
  }
  return DEFAULT_ESTIMATE_STATUSES
}

export const getOperationsStatuses = (config) => {
  if (config?.operations_statuses) {
    try { return JSON.parse(config.operations_statuses) } catch(e) {}
  }
  return DEFAULT_OPERATIONS_STATUSES
}

export const getPriorityOptions = (config) => {
  if (config?.priority_options) {
    try { return JSON.parse(config.priority_options) } catch(e) {}
  }
  return DEFAULT_PRIORITY_OPTIONS
}

export const getFurnitureClassOptions = (config) => {
  if (config?.furniture_class_options) {
    try { return JSON.parse(config.furniture_class_options) } catch(e) {}
  }
  return DEFAULT_FURNITURE_CLASS_OPTIONS
}

// Legacy compat — flat array of keys
export const CLIENT_STATUSES_KEYS = (config) => getClientStatuses(config).map(s => s.key)

// Get color for a status key
export const getStatusColor = (statuses, key) => {
  const s = statuses.find(s => s.key === key)
  return s?.color || '#94a3b8'
}

// Get label for a status key
export const getStatusLabel = (statuses, key) => {
  const s = statuses.find(s => s.key === key)
  return s?.label || key
}

// Check whether a transition from one status to another is "normal" (in
// the from-status's allowed_next array). Same key is always allowed
// (no-op). If allowed_next isn't defined, treats all transitions as
// allowed (config files that haven't been migrated to include the
// allowed_next field).
export const isTransitionAllowed = (statuses, fromKey, toKey) => {
  if (fromKey === toKey) return true
  const from = statuses.find(s => s.key === fromKey)
  if (!from) return true  // unknown source status — don't block
  if (!Array.isArray(from.allowed_next)) return true  // no rules defined
  return from.allowed_next.includes(toKey)
}

// Return the array of status keys that count as a "normal" next step
// from the given current status. Empty array means the status is
// terminal (no normal forward moves).
export const getNextStatuses = (statuses, fromKey) => {
  const from = statuses.find(s => s.key === fromKey)
  if (!from || !Array.isArray(from.allowed_next)) return []
  return from.allowed_next
}

// Text color (white or dark) based on bg brightness
export const statusTextColor = (bgColor) => {
  const hex = bgColor.replace('#', '')
  const r = parseInt(hex.substr(0, 2), 16), g = parseInt(hex.substr(2, 2), 16), b = parseInt(hex.substr(4, 2), 16)
  return (r * 0.299 + g * 0.587 + b * 0.114) > 160 ? '#1e293b' : '#ffffff'
}

// ── CREW CONSTANTS ──
export const CREW_ROLES = [
  { value: 'owner', label: 'Owner' },
  { value: 'crew_leader', label: 'Crew Leader' },
  { value: 'jr_crew_leader', label: 'Jr Crew Leader' },
  { value: 'new_recruit', label: 'New Recruit' },
  { value: 'driver', label: 'Driver' },
  { value: 'estimator', label: 'Estimator' },
]

export const SKILL_LEVELS = [
  { value: 1, label: 'Lvl 1', title: 'New Recruit' },
  { value: 2, label: 'Lvl 2', title: 'Rookie' },
  { value: 3, label: 'Lvl 3', title: 'Seasoned Crew Member' },
  { value: 4, label: 'Lvl 4', title: 'Jr Crew Leader' },
  { value: 5, label: 'Lvl 5', title: 'Crew Leader' },
]

export const CREW_COLORS = [
  '#3b82f6','#ef4444','#10b981','#f59e0b','#8b5cf6','#ec4899','#06b6d4','#f97316',
  '#6366f1','#14b8a6','#84cc16','#f43f5e','#0ea5e9','#a855f7','#d946ef','#e11d48',
  '#059669','#ca8a04','#dc2626','#7c3aed','#db2777','#0891b2','#c026d3','#ea580c',
  '#2563eb','#16a34a','#9333ea','#be123c','#0d9488','#b91c1c','#4f46e5','#65a30d',
  '#334155','#78716c','#57534e','#44403c'
]

export const getSkillLevel = (lvl) => SKILL_LEVELS.find(s => s.value === lvl) || SKILL_LEVELS[0]

// Map legacy role values from old data to current roles
export const LEGACY_ROLE_MAP = { 'lead': 'crew_leader', 'mover': 'new_recruit', 'crew_member': 'new_recruit', 'sr_crew_lead': 'crew_leader' }

export const normalizeRole = (role) => LEGACY_ROLE_MAP[role] || role

export const getCrewRole = (role) => CREW_ROLES.find(r => r.value === normalizeRole(role))?.label || role

export const MOVING_SKILL_TITLES = { 1: 'New Recruit', 2: 'Rookie', 3: 'Seasoned Crew', 4: 'Jr Crew Leader', 5: 'Crew Leader' }

export const SKILL_CATEGORIES = [
  { key: 'skill_moving', label: 'Moving', icon: '🚚', color: 'red', getTitle: (lvl) => MOVING_SKILL_TITLES[Number(lvl)] || '' },
  { key: 'skill_packing', label: 'Packing', icon: '📦', color: 'pink', getTitle: (lvl) => `Packing Lvl ${lvl}` },
]

export const getMovingTitle = (lvl) => MOVING_SKILL_TITLES[Number(lvl)] || `Moving ${lvl}`

export const getCrewSkillSummary = (member) => {
  const skills = []
  if ((member.skill_moving || 0) > 0) skills.push(getMovingTitle(member.skill_moving))
  if ((member.skill_packing || 0) > 0) skills.push(`Packing Lvl ${member.skill_packing}`)
  return skills.length > 0 ? skills.join(' · ') : 'No skills set'
}

export const getTopSkillLevel = (member) => Math.max(member.skill_moving || 0, member.skill_packing || 0, member.skill_level || 1)

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export const ROOM_TAGS = ['Living Room', 'Bedroom', 'Kitchen', 'Bathroom', 'Garage', 'Office', 'Dining Room', 'Basement', 'Attic', 'Outdoor', 'Storage', 'Other']

export const PHOTO_SOURCES = [
  { value: 'office', label: 'Office Upload' },
  { value: 'client', label: 'Client Submitted' },
  { value: 'estimator', label: 'Estimator Walkthrough' },
]

// Packing options and labels
export const PACKING_OPTIONS = [
  { value: 'none', label: 'No Packing' },
  { value: 'partial', label: 'Partial Packing' },
  { value: 'full', label: 'Full Packing' },
]

export const PACKING_LABELS = {
  'none': 'No Packing',
  'partial': 'Partial Packing',
  'full': 'Full Packing',
}

// Driveway and walk distance options
export const DRIVEWAY_OPTIONS = [
  { value: 'direct', label: 'Direct to Door' },
  { value: 'driveway', label: 'In Driveway' },
  { value: 'street', label: 'Street Parking' },
  { value: 'complex', label: 'Complex Lot' },
]

export const WALK_DISTANCE_OPTIONS = [
  { value: 'very_close', label: 'Very Close (< 50 ft)' },
  { value: 'close', label: 'Close (50-100 ft)' },
  { value: 'moderate', label: 'Moderate (100-200 ft)' },
  { value: 'far', label: 'Far (200+ ft)' },
]
